import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { 
  ArrowLeft, 
  Save, 
  Eye, 
  Settings, 
  Palette,
  Monitor,
  Tablet,
  Smartphone,
  Globe,
  Share,
  Download
} from 'lucide-react';
import VisualPageBuilder from '@/components/admin/VisualPageBuilder';
import { PageData } from '@/components/admin/VisualPageBuilder';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';

interface TenantLandingPage {
  id: string;
  tenant_id?: string; // superadmin: needed to resolve tenant slug
  template_id: string | null;
  name: string;
  slug: string;
  content_data: any;
  referrer_message: string;
  is_active: boolean;
  is_default: boolean;
  created_at: string;
}

export default function FullscreenBuilder() {
  const { pageId } = useParams<{ pageId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [page, setPage] = useState<TenantLandingPage | null>(null);
  const [pageData, setPageData] = useState<PageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('builder');
  const [tenantSlug, setTenantSlug] = useState('');
  
  // Page settings buffer
  const [pageSettings, setPageSettings] = useState({
    name: '',
    slug: '',
    referrer_message: '',
    is_active: true
  });

  useEffect(() => {
    if (pageId && user) {
      loadPage();
    }
  }, [pageId, user]);

  const loadPage = async () => {
    try {
      // Resolve tenant slug (works for Admin and Superadmin)
      let resolvedSlug = '';
      const { data: adminTenant } = await supabase
        .from('tenants')
        .select('id, slug')
        .eq('admin_user_id', user!.id)
        .single();

      if (adminTenant?.slug) {
        resolvedSlug = adminTenant.slug;
      }

      // Load the landing page
      const { data: pageData, error } = await supabase
        .from('tenant_landing_pages')
        .select('*')
        .eq('id', pageId)
        .single();

      if (error) throw error;

      setPage(pageData);
      
      // If no slug from admin tenant, resolve via page's tenant_id (superadmin path)
      if (!resolvedSlug && (pageData as any)?.tenant_id) {
        const { data: pageTenant } = await supabase
          .from('tenants')
          .select('slug')
          .eq('id', (pageData as any).tenant_id)
          .single();
        if (pageTenant?.slug) {
          resolvedSlug = pageTenant.slug;
        }
      }
      let contentData: PageData;
      if (pageData.content_data && 
          typeof pageData.content_data === 'object' && 
          !Array.isArray(pageData.content_data)) {
        const data = pageData.content_data as any;
        contentData = {
          elements: Array.isArray(data.elements) ? data.elements : [],
          globalStyles: data.globalStyles || {
            colors: {
              primary: "#3b82f6",
              secondary: "#6b7280",
              accent: "#10b981",
              background: "#ffffff",
              text: "#111827"
            },
            fonts: {
              primary: "Inter",
              secondary: "Inter"
            },
            spacing: {
              small: "8px",
              medium: "16px",
              large: "32px"
            }
          },
          responsive: data.responsive || {
            breakpoints: {
              mobile: "768px",
              tablet: "1024px",
              desktop: "1280px"
            }
          }
        };
      } else {
        contentData = { 
          elements: [], 
          globalStyles: {
            colors: {
              primary: "#3b82f6",
              secondary: "#6b7280", 
              accent: "#10b981",
              background: "#ffffff",
              text: "#111827"
            },
            fonts: {
              primary: "Inter",
              secondary: "Inter"
            },
            spacing: {
              small: "8px",
              medium: "16px",
              large: "32px"
            }
          },
          responsive: {
            breakpoints: {
              mobile: "768px",
              tablet: "1024px",
              desktop: "1280px"
            }
          }
        };
      }
      
      setPageData(contentData);
      setPageSettings({
        name: pageData.name,
        slug: pageData.slug,
        referrer_message: pageData.referrer_message,
        is_active: pageData.is_active
      });
      setTenantSlug(resolvedSlug);

    } catch (error) {
      console.error('Error loading page:', error);
      toast({
        title: 'Fehler',
        description: 'Seite konnte nicht geladen werden.',
        variant: 'destructive'
      });
      navigate('/admin/landing-pages');
    } finally {
      setLoading(false);
    }
  };

  const savePage = async () => {
    if (!page || !pageData) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from('tenant_landing_pages')
        .update({
          name: pageSettings.name,
          slug: pageSettings.slug,
          referrer_message: pageSettings.referrer_message,
          is_active: pageSettings.is_active,
          content_data: JSON.parse(JSON.stringify(pageData)),
          updated_at: new Date().toISOString()
        })
        .eq('id', page.id);

      if (error) throw error;

      toast({
        title: 'Gespeichert',
        description: 'Alle Änderungen wurden erfolgreich gespeichert.'
      });

    } catch (error) {
      console.error('Error saving page:', error);
      toast({
        title: 'Fehler',
        description: 'Seite konnte nicht gespeichert werden.',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleBuilderSave = (newPageData: PageData) => {
    setPageData(newPageData);
  };

  const handlePreview = () => {
    console.log('Preview clicked - tenantSlug:', tenantSlug, 'pageSettings.slug:', pageSettings.slug);
    if (tenantSlug && pageSettings.slug) {
      const url = `${window.location.origin}/lp/${tenantSlug}/${pageSettings.slug}`;
      console.log('Opening preview URL:', url);
      window.open(url, '_blank');
    } else {
      toast({
        title: 'Fehler',
        description: 'Seite kann noch nicht geöffnet werden. Bitte speichern Sie zuerst.',
        variant: 'destructive'
      });
    }
  };

  const copyPageUrl = () => {
    console.log('Copy URL clicked - tenantSlug:', tenantSlug, 'pageSettings.slug:', pageSettings.slug);
    if (tenantSlug && pageSettings.slug) {
      const url = `${window.location.origin}/lp/${tenantSlug}/${pageSettings.slug}`;
      console.log('Copying URL:', url);
      navigator.clipboard.writeText(url).then(() => {
        toast({
          title: 'URL kopiert',
          description: 'Landing Page URL wurde in die Zwischenablage kopiert.'
        });
      }).catch((err) => {
        console.error('Failed to copy URL:', err);
        toast({
          title: 'Fehler',
          description: 'URL konnte nicht kopiert werden.',
          variant: 'destructive'
        });
      });
    } else {
      toast({
        title: 'Fehler',
        description: 'URL kann noch nicht kopiert werden. Bitte speichern Sie zuerst.',
        variant: 'destructive'
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Builder wird geladen...</p>
        </div>
      </div>
    );
  }

  if (!page || !pageData) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground">Seite nicht gefunden</p>
          <Button 
            onClick={() => navigate('/admin/landing-pages')}
            className="mt-4"
          >
            Zurück zur Übersicht
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Top Navigation Bar */}
      <header className="border-b bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="flex items-center justify-between px-6 py-3">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/admin/landing-pages')}
              className="gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Zurück
            </Button>
            
            <div className="flex items-center gap-2">
              <Badge variant="secondary">Landing Page Builder</Badge>
              <h1 className="text-lg font-semibold">{page.name}</h1>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={copyPageUrl}>
              <Share className="w-4 h-4 mr-2" />
              URL teilen
            </Button>
            
            <Button variant="outline" size="sm" onClick={handlePreview}>
              <Eye className="w-4 h-4 mr-2" />
              Vorschau
            </Button>
            
            <Button 
              onClick={savePage} 
              disabled={saving}
              size="sm"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Speichert...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Speichern
                </>
              )}
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
          <TabsList className="justify-start w-auto mx-6 mt-4">
            <TabsTrigger value="builder" className="gap-2">
              <Palette className="w-4 h-4" />
              Visual Builder
            </TabsTrigger>
            <TabsTrigger value="settings" className="gap-2">
              <Settings className="w-4 h-4" />
              Einstellungen
            </TabsTrigger>
            <TabsTrigger value="preview" className="gap-2">
              <Globe className="w-4 h-4" />
              Live Vorschau
            </TabsTrigger>
          </TabsList>

          {/* Builder Tab */}
          <TabsContent value="builder" className="flex-1 m-0 p-0">
            <div className="h-[calc(100vh-120px)]">
              <VisualPageBuilder
                initialData={pageData}
                onSave={handleBuilderSave}
                onPreview={() => {}}
              />
            </div>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="flex-1 p-6">
            <div className="max-w-2xl space-y-6">
              <div className="space-y-4">
                <h2 className="text-xl font-semibold">Landing Page Einstellungen</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Name der Landing Page</Label>
                    <Input
                      id="name"
                      value={pageSettings.name}
                      onChange={(e) => setPageSettings({ ...pageSettings, name: e.target.value })}
                      placeholder="z.B. Restaurant Empfehlung"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="slug">URL-Slug</Label>
                    <Input
                      id="slug"
                      value={pageSettings.slug}
                      onChange={(e) => setPageSettings({ 
                        ...pageSettings, 
                        slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') 
                      })}
                      placeholder="z.B. restaurant-empfehlung"
                    />
                    <p className="text-sm text-muted-foreground">
                      URL wird: /lp/{tenantSlug}/{pageSettings.slug}
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="referrer-message">Empfehler-Nachricht</Label>
                  <Textarea
                    id="referrer-message"
                    value={pageSettings.referrer_message}
                    onChange={(e) => setPageSettings({ ...pageSettings, referrer_message: e.target.value })}
                    placeholder="Verwenden Sie {referrer_name} für den Namen"
                    rows={3}
                  />
                  <p className="text-sm text-muted-foreground">
                    {`{referrer_name}`} wird durch den Namen des Empfehlers ersetzt
                  </p>
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    checked={pageSettings.is_active}
                    onCheckedChange={(checked) => setPageSettings({ ...pageSettings, is_active: checked })}
                  />
                  <Label>Landing Page ist aktiv</Label>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Preview Tab */}
          <TabsContent value="preview" className="flex-1 p-6">
            <div className="text-center space-y-4">
              <h2 className="text-xl font-semibold">Live Vorschau</h2>
              <p className="text-muted-foreground">
                Öffnen Sie die Landing Page in einem neuen Tab, um sie in Aktion zu sehen.
              </p>
              
              <div className="flex justify-center gap-4">
                <Button onClick={handlePreview} size="lg">
                  <Eye className="w-5 h-5 mr-2" />
                  Vorschau öffnen
                </Button>
                
                <Button variant="outline" onClick={copyPageUrl} size="lg">
                  <Share className="w-5 h-5 mr-2" />
                  URL kopieren
                </Button>
              </div>

              <div className="mt-8 p-4 bg-muted/50 rounded-lg">
                <p className="text-sm text-muted-foreground">
                  <strong>URL:</strong> /lp/{tenantSlug}/{pageSettings.slug}
                </p>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}